import axios from "axios";
import config from "../config";
import Labels from "../services/apiLabels";

export const REFERRAL_COPY = {
  copied:
    "Referral link copied. Share it so a friend can sign up with your code.",
  copiedNoCode:
    "Signup link copied. Sign out and sign in again to load your personal referral code.",
  noCode:
    "No referral code is on this account yet. Sign out and sign in again. The code is created with your account, not by sending an email.",
  linkWithCode:
    "Anyone who opens this link lands on signup with your code already applied.",
  linkWithoutCode:
    "This is a generic signup link until your personal referral code is on the account. Sign out and sign in again to load it.",
  emailHint:
    "We’ll email your referral link to this person from AutoTrade Port.",
  emailSent: "Referral email sent.",
  emailFailed: "Could not send the referral email.",
  noResponse:
    "No response from the invite service. Check your connection and try again.",
  emptyResponse:
    "The invite service returned no confirmation. The email may not have been sent.",
  timeout: "The invite service did not respond in time. Try again.",
};

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export function normalizeInviteEmail(value) {
  return String(value || "").trim();
}

export function getInviteEmailError(value) {
  const email = normalizeInviteEmail(value);
  if (!email) return "Please enter an email address.";
  if (!isValidEmail(email)) return "Please enter a valid email address.";
  return "";
}

function isJsonContentType(headers) {
  const type = String(
    headers?.["content-type"] || headers?.["Content-Type"] || ""
  );
  return type.includes("application/json");
}

function firstString(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) return firstString(value[0]);
  return "";
}

function pickApiText(data) {
  if (data == null || data === "") return "";
  if (typeof data === "string") {
    const text = data.trim();
    if (!text || text.startsWith("<")) return "";
    return text;
  }
  if (typeof data !== "object") return String(data);

  const fieldError =
    firstString(data.email) ||
    firstString(data.non_field_errors) ||
    firstString(data.detail) ||
    firstString(data.error);
  if (fieldError) return fieldError;

  const message = data.message;
  if (typeof message === "string" && message.trim()) return message.trim();
  if (message && typeof message === "object") {
    const nested =
      firstString(message.description) ||
      firstString(message.heading) ||
      firstString(message.detail) ||
      firstString(message.text);
    if (nested) return nested;
  }

  if (data.data && data.data !== data) {
    const inner = pickApiText(data.data);
    if (inner) return inner;
  }

  if (Array.isArray(data.errors) && data.errors[0]) {
    return firstString(data.errors[0]) || pickApiText(data.errors[0]);
  }

  return "";
}

function fallbackForStatus(status) {
  if (status === 400 || status === 422) {
    return "The invite could not be sent. Check the email address and try again.";
  }
  if (status === 401 || status === 403) {
    return "Please log in again, then send the invite.";
  }
  if (status === 404 || status === 405) {
    return "The invite service did not respond. Try again later.";
  }
  if (status === 429) {
    return "Too many invite attempts. Wait a moment and try again.";
  }
  if (status >= 500) {
    return "The invite service returned an error. Try again later.";
  }
  return REFERRAL_COPY.emailFailed;
}

function isCodeGeneratedCopy(text) {
  const value = String(text || "").toLowerCase();
  return (
    value.includes("referral code generated") ||
    value.includes("code generated successfully")
  );
}

function successMessageForInvite(apiText, email) {
  if (apiText && !isCodeGeneratedCopy(apiText)) {
    const value = apiText.toLowerCase();
    if (
      value.includes("email") ||
      value.includes("invite") ||
      value.includes("sent")
    ) {
      return apiText;
    }
  }
  return email
    ? `Referral email sent to ${email}.`
    : REFERRAL_COPY.emailSent;
}

function errorMessageForInvite(apiText, status) {
  if (apiText && !isCodeGeneratedCopy(apiText)) return apiText;
  return fallbackForStatus(status);
}

function toError(message, code) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function errorFromAxios(error) {
  if (error.code === "ECONNABORTED") {
    return toError(REFERRAL_COPY.timeout, "timeout");
  }
  if (!error.response) {
    return toError(REFERRAL_COPY.noResponse, "no_response");
  }

  const { status, data, headers } = error.response;
  const fromApi = isJsonContentType(headers) ? pickApiText(data) : "";
  return toError(errorMessageForInvite(fromApi, status), "http");
}

export async function sendReferralInvite({ email, token }) {
  const cleaned = normalizeInviteEmail(email);
  const validation = getInviteEmailError(cleaned);
  if (validation) throw toError(validation, "validation");
  if (!token) throw toError("Please log in again.", "auth");

  let response;
  try {
    response = await axios.post(
      `${config.baseUrl}${Labels.send_referral}`,
      { email: cleaned },
      {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
        timeout: 20000,
      }
    );
  } catch (error) {
    if (error.code === "validation" || error.code === "auth") throw error;
    throw errorFromAxios(error);
  }

  if (!response) {
    throw toError(REFERRAL_COPY.noResponse, "no_response");
  }

  const data = response.data;
  if (data == null || data === "") {
    throw toError(REFERRAL_COPY.emptyResponse, "empty");
  }

  if (!isJsonContentType(response.headers) && typeof data !== "object") {
    throw toError(REFERRAL_COPY.emptyResponse, "empty");
  }

  const apiText = pickApiText(data);
  if (data?.message?.type === "error") {
    throw toError(
      errorMessageForInvite(apiText, response.status),
      "api"
    );
  }

  const looksSuccessful =
    data?.message?.type === "success" ||
    data?.success === true ||
    data?.status === true ||
    data?.status === "success";

  if (looksSuccessful || (response.status >= 200 && response.status < 300)) {
    if (looksSuccessful || apiText) {
      return {
        message: successMessageForInvite(apiText, cleaned),
        data,
      };
    }
    throw toError(REFERRAL_COPY.emptyResponse, "empty");
  }

  throw toError(
    errorMessageForInvite(apiText, response.status),
    "http"
  );
}
